import { useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

import { TabIndexEnum } from '@modules/ContactManager/components/Header/interfaces';
import { queryParams, withQueryParams } from '@shared/commonFunctions';
import { ILendingLoader } from '@shared/components/IlendingLoader';
import { useTabs } from '@shared/hooks/useTabs';

import { requestFromLiveVox, requestGetNewDealSheet } from './requests';
import { IDialerResponse, IManagerTab } from './interfaces';

export const Dialer = ({livevox = false}) => {
  const params = queryParams();
  const navigate = useNavigate();
  const { openTab } = useTabs();

  const openContactManagerTab = useCallback(({ contactId, tab, leadId, title }: IManagerTab) => {
      const route = withQueryParams('/contact-manager', {
        id: contactId,
        tab,
        leadId
      }, false);
      openTab({ title: title ?? '', route, replaceIfNotExist: route.includes('search-results') });
      navigate(route);
  },[openTab, navigate, withQueryParams]);

  const openSearchTab = useCallback((search : string) => {
    const route = withQueryParams('/search-results', {
      searchText: search.trim(),
    }, false);
    navigate(route);
  },[navigate, withQueryParams]);

  const handleForbiddenResponse = useCallback(() => {
      if (params?.id) openContactManagerTab({ contactId: params?.id });
      else if (params?.phoneNumber) openSearchTab(params?.phoneNumber);
  },[openContactManagerTab, openSearchTab]);

  const handleResponse = useCallback((response: IDialerResponse) => {
      if (response?.status === 403) handleForbiddenResponse();
      else if (response?.contactId) {
        openContactManagerTab({
          contactId: response?.contactId,
          leadId: response?.leadId,
          tab: TabIndexEnum.Leads,
        });
      } else {
        navigate('/');
      }
  },[handleForbiddenResponse, openContactManagerTab, navigate]);

  const getData = useCallback(
    async () => {
      const result = livevox
        ? await requestFromLiveVox(params)
        : await requestGetNewDealSheet(params?.id ?? '');
      handleResponse(result?.response ?? result);
  },[params, livevox, handleResponse]);

  useEffect(() => {
    getData();
  }, []);

  return <ILendingLoader />;
};